import { useDropzone } from 'react-dropzone'
import { Upload } from 'lucide-react'

import type { CompressionType } from '@/api/uploadFile'
import { IMAGE_EXTS } from '../../lib/imageFormats'
import type { BlockUploadTarget } from './uploadTask'
import { useImageDrop } from './useImageDrop'
import { useCanvasBridge } from './useCanvasBridge'
import type { UploadBridge } from '@/features/editor/lib/uploadBridge'
import { ImageUploadFilled } from './ImageUploadFilled'
import { ImageUploadEmptyState } from './ImageUploadEmptyState'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  value: string | null
  /** Может вернуть промис с успехом сохранения — тогда старый блоб удаляется только после
   *  прошедшего сохранения. Синхронный (void) onChange считается успешным, как и раньше. */
  onChange: (url: string | null) => void | Promise<boolean>
  compressionType?: CompressionType
  accept?: Record<string, string[]>
  label?: string
  disabled?: boolean
  /**
   * Место картинки принадлежит БЛОКУ урока. Пока этот компонент на экране,
   * ничего не меняется: URL уходит в `onChange` (черновик), сохраняет кнопка
   * панели. Цель нужна на случай, когда автор ушёл, не дождавшись загрузки, —
   * тогда очередь сохранит картинку сама, собрав тело `PUT` из свежего
   * серверного блока (п.24). Без пропа (курс, урок, категория, статья) фонового
   * пути нет вовсе.
   */
  blockTarget?: BlockUploadTarget
  /** Зона и превью строго 16:9 (баннер курса): картинка заполняет её, обрезаясь по краям. */
  widescreen?: boolean
  /** `canvas` — пустая зона на светлом экране урока (составной блок). */
  tone?: 'app' | 'canvas'
  /** Холст принимает файл за эту зону (`createUploadBridge`): клик и перетаскивание
   *  на пустом экране грузятся здесь, прогресс уходит обратно на холст. */
  canvasBridge?: UploadBridge
}

export const ImageUpload = ({
  value,
  onChange,
  compressionType = 'banner_compression',
  accept = { 'image/*': IMAGE_EXTS },
  label,
  disabled = false,
  blockTarget,
  widescreen = false,
  tone = 'app',
  canvasBridge,
}: Props) => {
  const m = useMessages(messages)
  const { onDrop, handleRemove, progress } = useImageDrop({
    value,
    onChange,
    compressionType,
    disabled,
    blockTarget,
  })

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept,
    multiple: false,
    disabled,
  })
  const { fromCanvas } = useCanvasBridge(canvasBridge, {
    onDrop,
    open,
    onRemove: () => void handleRemove(),
    progress,
    disabled,
  })
  // Грузится с холста — полоса там, здесь её не дублируем.
  const shownProgress = fromCanvas ? null : progress

  return (
    <div className="space-y-2">
      {value ? (
        <ImageUploadFilled
          value={value}
          widescreen={widescreen}
          disabled={disabled}
          busy={progress !== null}
          shownProgress={shownProgress}
          inputProps={getInputProps()}
          onReplace={open}
          onRemove={() => void handleRemove()}
        />
      ) : (
        <div className={widescreen ? 'aspect-video short:aspect-auto short:h-40' : undefined}>
          <ImageUploadEmptyState
            icon={Upload}
            label={label ?? m.imageUploadLabel}
            rootProps={getRootProps()}
            inputProps={getInputProps()}
            disabled={disabled}
            fill={widescreen}
            uploading={shownProgress !== null}
            progress={shownProgress}
            subtitle={widescreen ? m.imageFormatsWide : undefined}
            tone={tone}
          />
        </div>
      )}
    </div>
  )
}
